import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    managementFeesApi,
    ManagementFeeClientInfo,
    ManagementFeeQuarter,
} from "@/services/api";
import { formatCurrency, formatNumber, formatDate } from "@/utils/formatters";
import { getCurrentFYQuarter, formatQuarterLabel } from "@/utils/fy";
import tableStyles from "@/components/utils/shared.module.css";
import { Download } from "lucide-react";
import { FEE_BUFFER_PCT } from "@/config/constants";
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder, StatGrid } from "@/components/ui";
import { DetailSection, DetailField } from "@/components/utils/PageCard";
import styles from "./ManagementFees.module.css";

const ManagementFeeDetail: React.FC = () => {
    const { clientCode } = useParams<{ clientCode: string }>();
    const navigate = useNavigate();
    const [clientInfo, setClientInfo] = useState<ManagementFeeClientInfo | null>(null);
    const [quarters, setQuarters] = useState<ManagementFeeQuarter[]>([]);
    const [expandedQuarter, setExpandedQuarter] = useState<string | null>(null);
    const [showMethodology, setShowMethodology] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!clientCode) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        managementFeesApi.getClientDetail(clientCode)
            .then(response => {
                if (!cancelled) {
                    setClientInfo(response.client_info);
                    setQuarters(response.quarters);
                }
            })
            .catch(err => {
                if (!cancelled) {
                    const apiErr = err as { response?: { data?: { error?: string } } };
                    setError(apiErr?.response?.data?.error || "Failed to load client fee detail");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [clientCode]);

    const toggleQuarter = (quarter: string) => {
        setExpandedQuarter(prev => (prev === quarter ? null : quarter));
    };

    const fmt = (val: number | null) => (val != null ? formatNumber(val, 2) : "—");
    const fmtCur = (val: number | null) => (val != null ? formatCurrency(val) : "—");

    const sumDaily = (items: { daily_fee: number | null; daily_fee_gst: number | null }[]) => {
        let fee = 0, feeGst = 0;
        for (const d of items) {
            if (d.daily_fee != null) fee += d.daily_fee;
            if (d.daily_fee_gst != null) feeGst += d.daily_fee_gst;
        }
        return { fee, feeGst };
    };

    const downloadCSV = () => {
        if (!quarters.length || !clientInfo) return;
        const headers = [
            `Client: ${clientInfo.client_code}`, `Name: ${clientInfo.client_name}`,
            `Scheme: ${clientInfo.scheme_code || ""}`, `Mgmt Fee: ${clientInfo.management_fee_pct || ""}%`, "", "",
        ];
        const empty = ",,,,,";
        const fmtDaily = (d: { date: string; aum: number | null; mgmt_pct: number | null; daily_fee: number | null; daily_fee_gst: number | null }, type: string) =>
            ["", d.date, type, d.aum ?? "", d.mgmt_pct != null ? `${d.mgmt_pct}%` : "", d.daily_fee ?? "", d.daily_fee_gst ?? ""].join(",");

        const csvRows = quarters.flatMap(q => [
            empty,
            `${q.quarter} (${q.start_date} to ${q.end_date}),,,,,`,
            empty,
            `,, NUVAMA CHARGEABLE,Avg AUM: ${q.nuvama_avg_aum ?? ""},Total Fee: ${q.nuvama_fee ?? ""},Total Fee GST: ${q.nuvama_fee_gst ?? ""},,`,
            ",Date,Type,AUM,Fee Rate (%),Daily Fee,Daily Fee (GST)",
            ...q.daily_nuvama.map(d => fmtDaily(d, "Nuvama")),
            empty,
            `,, HOLDING AUM,Avg AUM: ${q.holding_avg_aum ?? ""},Total Fee: ${q.holding_fee ?? ""},Total Fee GST: ${q.holding_fee_gst ?? ""},,`,
            ",Date,Type,AUM,Fee Rate (%),Daily Fee,Daily Fee (GST)",
            ...q.daily_holding.map(d => fmtDaily(d, "Holding")),
            empty,
            empty,
        ]);
        const csv = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `management_fees_${clientInfo.client_code}_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    if (loading) {
        return (
            <PageFrame header={null}>
                <Surface>
                    <Placeholder kind="loading" message="Loading client fee detail..." />
                </Surface>
            </PageFrame>
        );
    }

    if (error) {
        return (
            <PageFrame header={null}>
                <Note
                    tone="error"
                    action={
                        <Button size="sm" onClick={() => navigate("/management-fees")}>
                            Back to List
                        </Button>
                    }
                >
                    {error}
                </Note>
            </PageFrame>
        );
    }

    const cashPctOfAum = (() => {
        if (!clientInfo) return null;
        const currentQ = quarters.find(q => q.quarter === getCurrentFYQuarter());
        const currentAum = currentQ?.holding_avg_aum || currentQ?.nuvama_avg_aum;
        if (clientInfo.cash_plus_liquid != null && currentAum && currentAum > 0) {
            const pct = (clientInfo.cash_plus_liquid / currentAum) * 100;
            return <span className={styles['client-info-pct']}> ({formatNumber(pct, 2)}% of AUM)</span>;
        }
        return null;
    })();

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Management Fee Detail"
                    actions={
                        <>
                            <Button onClick={() => navigate(-1)}>
                                ← Back
                            </Button>
                            <Button
                                onClick={downloadCSV}
                                disabled={!quarters.length}
                                icon={<Download size={16} />}
                            >
                                Export CSV
                            </Button>
                        </>
                    }
                />
            }
        >
                {/* Client Info Card */}
                {clientInfo && (
                    <Surface padded>
                        <DetailSection>
                            <DetailField label="Client Code" value={clientInfo.client_code} />
                            <DetailField label="Client Name" value={clientInfo.client_name} />
                            <DetailField label="Scheme" value={clientInfo.scheme_code || "—"} />
                            <DetailField label="Strategy" value={clientInfo.strategy_name || "—"} />
                        </DetailSection>
                        <StatGrid
                            items={[
                                { label: "Management Fee", value: `${clientInfo.management_fee_pct ?? "—"}%` },
                                ...(clientInfo.distributor_share_pct != null
                                    ? [{ label: "Distributor Share", value: `${clientInfo.distributor_share_pct.toFixed(2)}%` }]
                                    : []),
                                {
                                    label: "Cash + Liquid",
                                    value: <>{fmtCur(clientInfo.cash_plus_liquid ?? null)}{cashPctOfAum}</>,
                                },
                            ]}
                        />
                        <div className={styles['client-info-disclaimer']}>
                            * Cash &amp; Liquid balance{clientInfo.cash_date ? ` as of ${formatDate(clientInfo.cash_date)}` : ""} includes bank cash, clearing cash, and liquid ETF holdings, net of utilised cash and blocked amounts.
                            {" "}The management fee rate{clientInfo.management_fee_valid_from ? ` (effective ${formatDate(clientInfo.management_fee_valid_from)})` : ""} is date-windowed — if the rate changes mid-quarter, each day uses the rate that was active on that specific date. The daily breakdown table shows the applied rate per day; rows where the rate changed are highlighted in amber.
                            {" "}Insufficient cash alerts include a {(FEE_BUFFER_PCT * 100).toFixed(0)}% buffer above the fee (incl. GST) to account for potential fee fluctuations during the quarter.
                        </div>
                    </Surface>
                )}

                {/* Low-cash alert — only for current quarter */}
                {clientInfo && quarters.length > 0 && (() => {
                    const currentQ = quarters.find(q => q.quarter === getCurrentFYQuarter());
                    if (!currentQ) return null;
                    const q = currentQ;
                    const cash = clientInfo.cash_plus_liquid ?? null;
                    const nuvamaBuffered = q.nuvama_fee_gst != null ? q.nuvama_fee_gst * (1 + FEE_BUFFER_PCT) : null;
                    const holdingBuffered = q.holding_fee_gst != null ? q.holding_fee_gst * (1 + FEE_BUFFER_PCT) : null;
                    const nuvamaShort = cash != null && nuvamaBuffered != null && cash < nuvamaBuffered;
                    const holdingShort = cash != null && holdingBuffered != null && cash < holdingBuffered;
                    if (!nuvamaShort && !holdingShort) return null;
                    return (
                        <Note tone="warning">
                            <span style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <strong>Insufficient Cash</strong>
                                <span>Cash + Liquid ({fmtCur(cash)}) is below the current quarter fee (incl. GST) + {(FEE_BUFFER_PCT * 100).toFixed(0)}% buffer.</span>
                                {nuvamaShort && <span>Nuvama Fee (incl. GST): {fmtCur(q.nuvama_fee_gst)} — Buffered Threshold: {fmtCur(nuvamaBuffered)} — Shortfall: {fmtCur(nuvamaBuffered! - cash!)}</span>}
                                {holdingShort && <span>Holding Fee (incl. GST): {fmtCur(q.holding_fee_gst)} — Buffered Threshold: {fmtCur(holdingBuffered)} — Shortfall: {fmtCur(holdingBuffered! - cash!)}</span>}
                            </span>
                        </Note>
                    );
                })()}

                {/* Fee Methodology */}
                <div className={styles['mgmt-fees-methodology']}>
                    <Button
                        variant="ghost"
                        aria-expanded={showMethodology}
                        onClick={() => setShowMethodology(p => !p)}
                        style={{ width: "100%", justifyContent: "space-between", borderRadius: 0 }}
                    >
                        <span>ℹ How fees are calculated</span>
                        <span>{showMethodology ? "▲" : "▼"}</span>
                    </Button>
                    {showMethodology && (
                        <div className={styles['mgmt-fees-methodology-body']}>
                            <div className={styles['methodology-track']}>
                                <div className={`${styles['methodology-track-header']} ${styles.nuvama}`}>Nuvama Chargeable AUM</div>
                                <div className={styles['methodology-steps']}>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Source</span>
                                        <span>Daily chargeable AUM values reported by Nuvama, keyed by FA account number</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Holidays</span>
                                        <span>Weekends and market holidays carry forward the previous trading day&apos;s AUM (forward-fill)</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Daily Fee</span>
                                        <span><code>Daily AUM × Mgmt Fee % ÷ Days in Year</code> — leap years use 366, others 365</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles['methodology-track']}>
                                <div className={`${styles['methodology-track-header']} ${styles.holding}`}>Holding AUM</div>
                                <div className={styles['methodology-steps']}>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Source</span>
                                        <span>Portfolio Holdings — computed daily as <code>SUM(Qty × Price × FX Rate + Accrued Income × FX Rate)</code> across all positions</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Holidays</span>
                                        <span>Same forward-fill logic — non-trading days inherit the last available holding value</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Daily Fee</span>
                                        <span><code>Daily AUM × Mgmt Fee % ÷ Days in Year</code></span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles['methodology-track']}>
                                <div className={`${styles['methodology-track-header']} ${styles.distribution}`}>Distributor Share</div>
                                <div className={styles['methodology-steps']}>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Source</span>
                                        <span>Distributor Share % set on the client profile — the fraction of the management fee paid to the distributor</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Nuvama Dist</span>
                                        <span><code>Nuvama Fee × Distributor Share %</code> — portion of the Nuvama chargeable fee allocated to the distributor</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Holding Dist</span>
                                        <span><code>Holding Fee × Distributor Share %</code> — portion of the Holding AUM fee allocated to the distributor</span>
                                    </div>
                                    <div className={styles['methodology-step']}>
                                        <span className={styles['methodology-step-label']}>Note</span>
                                        <span>Distribution is a sub-allocation of the management fee — it does not increase the client&apos;s total fee. Clients with no distributor show 0.</span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles['methodology-summary-row']}>
                                <div className={styles['methodology-summary-item']}>
                                    <span className={styles['methodology-summary-label']}>Quarterly Fee</span>
                                    <span className={styles['methodology-summary-desc']}>Sum of all daily fees within the quarter</span>
                                </div>
                                <div className={styles['methodology-summary-item']}>
                                    <span className={styles['methodology-summary-label']}>Avg AUM</span>
                                    <span className={styles['methodology-summary-desc']}>Mean of daily AUM values across the quarter (incl. forward-filled days)</span>
                                </div>
                                <div className={styles['methodology-summary-item']}>
                                    <span className={styles['methodology-summary-label']}>GST</span>
                                    <span className={styles['methodology-summary-desc']}>18% applied on top of the fee. Cash + Liquid is compared against the <strong>GST-inclusive</strong> fee plus a <strong>{FEE_BUFFER_PCT * 100}% buffer</strong> to determine sufficiency.</span>
                                </div>
                                <div className={styles['methodology-summary-item']}>
                                    <span className={styles['methodology-summary-label']}>Fee Rate</span>
                                    <span className={styles['methodology-summary-desc']}>Date-windowed — if the fee is changed mid-quarter, each day uses the rate that was active on that specific date. The daily breakdown table shows the applied rate per day; rows where the rate changed are highlighted in amber.</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Quarter History Table */}
                <Surface title="Quarter History">
                {quarters.length === 0 ? (
                    <Placeholder message="No quarterly data is available for this client." />
                ) : (
                    <div className={tableStyles['table-wrapper']}>
                        <div className={tableStyles['table-scroll']}>
                            <table className={tableStyles.table}>
                                <thead>
                                    <tr>
                                        <th>Quarter</th>
                                        <th className={tableStyles['text-right']}>Chargeable AUM (Avg)</th>
                                        <th className={tableStyles['text-right']}>Nuvama Fee (₹)</th>
                                        <th className={tableStyles['text-right']}>Nuvama Dist (₹)</th>
                                        <th className={tableStyles['text-right']}>Nuvama Fee GST (₹)</th>
                                        <th className={tableStyles['text-right']}>Holding AUM (Avg)</th>
                                        <th className={tableStyles['text-right']}>Holding Fee (₹)</th>
                                        <th className={tableStyles['text-right']}>Holding Dist (₹)</th>
                                        <th className={tableStyles['text-right']}>Holding Fee GST (₹)</th>
                                        <th style={{ width: "40px" }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quarters.map(q => (
                                        <React.Fragment key={q.quarter}>
                                            <tr
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleQuarter(q.quarter)}
                                            >
                                                <td><strong>{formatQuarterLabel(q.quarter)}</strong></td>
                                                <td className={tableStyles['text-right']}>{fmt(q.nuvama_avg_aum)}</td>
                                                <td className={tableStyles['text-right']}>{fmt(q.nuvama_fee)}</td>
                                                <td className={`${(q.nuvama_distribution_fee ?? 0) === 0 ? styles['dist-zero'] : ''} ${tableStyles['text-right']}`}>
                                                    {fmt(q.nuvama_distribution_fee ?? 0)}
                                                </td>
                                                <td className={tableStyles['text-right']}>{fmt(q.nuvama_fee_gst)}</td>
                                                <td className={tableStyles['text-right']}>{fmt(q.holding_avg_aum)}</td>
                                                <td className={tableStyles['text-right']}>{fmt(q.holding_fee)}</td>
                                                <td className={`${(q.holding_distribution_fee ?? 0) === 0 ? styles['dist-zero'] : ''} ${tableStyles['text-right']}`}>
                                                    {fmt(q.holding_distribution_fee ?? 0)}
                                                </td>
                                                <td className={tableStyles['text-right']}>{fmt(q.holding_fee_gst)}</td>
                                                <td style={{ textAlign: "left" }}>
                                                    {expandedQuarter === q.quarter ? "▲" : "▼"}
                                                </td>
                                            </tr>

                                            {expandedQuarter === q.quarter && (
                                                <tr>
                                                    <td colSpan={10} style={{ padding: 0 }}>
                                                        <div className={styles['mgmt-fees-daily-section']}>
                                                            <div className={styles['daily-tables-row']}>
                                                                {/* Nuvama daily */}
                                                                <div className={styles['daily-table-col']}>
                                                                    <h4>Nuvama Chargeable — Daily Breakdown</h4>
                                                                    <div className={styles['daily-table-scroll']}>
                                                                        <table className={`${tableStyles.table} ${styles['mgmt-fees-daily-table']}`}>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Date</th>
                                                                                    <th className={tableStyles['text-right']}>AUM</th>
                                                                                    <th className={tableStyles['text-right']}>Fee Rate (%)</th>
                                                                                    <th className={tableStyles['text-right']}>Daily Fee (₹)</th>
                                                                                    <th className={tableStyles['text-right']}>Daily Fee GST (₹)</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {q.daily_nuvama.length === 0 ? (
                                                                                    <tr><td colSpan={5} style={{ textAlign: "center", padding: "20px", color: "var(--text-faint)" }}>No data</td></tr>
                                                                                ) : (
                                                                                    q.daily_nuvama.map((d, i) => {
                                                                                        const prevRate = i > 0 ? q.daily_nuvama[i - 1].mgmt_pct : null;
                                                                                        const rateChanged = i > 0 && d.mgmt_pct !== prevRate;
                                                                                        return (
                                                                                            <tr key={d.date} className={rateChanged ? styles['daily-row-rate-change'] : undefined}>
                                                                                                <td>{formatDate(d.date)}</td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>{fmt(d.aum)}</td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>
                                                                                                    {d.mgmt_pct != null ? `${d.mgmt_pct}%` : "—"}
                                                                                                    {rateChanged && <span className={styles['rate-change-badge']} title="Fee rate changed">↑</span>}
                                                                                                </td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>{fmt(d.daily_fee)}</td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>{fmt(d.daily_fee_gst)}</td>
                                                                                            </tr>
                                                                                        );
                                                                                    })
                                                                                )}
                                                                            </tbody>
                                                                            {q.daily_nuvama.length > 0 && (() => {
                                                                                const totals = sumDaily(q.daily_nuvama);
                                                                                return (
                                                                                    <tfoot>
                                                                                        <tr className={styles['daily-table-total-row']}>
                                                                                            <td>Total</td>
                                                                                            <td></td>
                                                                                            <td></td>
                                                                                            <td className={styles['daily-total-value']}>{fmt(totals.fee)}</td>
                                                                                            <td className={styles['daily-total-value']}>{fmt(totals.feeGst)}</td>
                                                                                        </tr>
                                                                                    </tfoot>
                                                                                );
                                                                            })()}
                                                                        </table>
                                                                    </div>
                                                                </div>

                                                                {/* Holding daily */}
                                                                <div className={styles['daily-table-col']}>
                                                                    <h4>Holding AUM — Daily Breakdown</h4>
                                                                    <div className={styles['daily-table-scroll']}>
                                                                        <table className={`${tableStyles.table} ${styles['mgmt-fees-daily-table']}`}>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Date</th>
                                                                                    <th className={tableStyles['text-right']}>AUM</th>
                                                                                    <th className={tableStyles['text-right']}>Fee Rate (%)</th>
                                                                                    <th className={tableStyles['text-right']}>Daily Fee (₹)</th>
                                                                                    <th className={tableStyles['text-right']}>Daily Fee GST (₹)</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {q.daily_holding.length === 0 ? (
                                                                                    <tr><td colSpan={5} style={{ textAlign: "center", padding: "20px", color: "var(--text-faint)" }}>No data</td></tr>
                                                                                ) : (
                                                                                    q.daily_holding.map((d, i) => {
                                                                                        const prevRate = i > 0 ? q.daily_holding[i - 1].mgmt_pct : null;
                                                                                        const rateChanged = i > 0 && d.mgmt_pct !== prevRate;
                                                                                        return (
                                                                                            <tr key={d.date} className={rateChanged ? styles['daily-row-rate-change'] : undefined}>
                                                                                                <td>{formatDate(d.date)}</td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>{fmt(d.aum)}</td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>
                                                                                                    {d.mgmt_pct != null ? `${d.mgmt_pct}%` : "—"}
                                                                                                    {rateChanged && <span className={styles['rate-change-badge']} title="Fee rate changed">↑</span>}
                                                                                                </td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>{fmt(d.daily_fee)}</td>
                                                                                                <td className={`${styles['amount-cell']} ${tableStyles['text-right']}`}>{fmt(d.daily_fee_gst)}</td>
                                                                                            </tr>
                                                                                        );
                                                                                    })
                                                                                )}
                                                                            </tbody>
                                                                            {q.daily_holding.length > 0 && (() => {
                                                                                const totals = sumDaily(q.daily_holding);
                                                                                return (
                                                                                    <tfoot>
                                                                                        <tr className={styles['daily-table-total-row']}>
                                                                                            <td>Total</td>
                                                                                            <td></td>
                                                                                            <td></td>
                                                                                            <td className={styles['daily-total-value']}>{fmt(totals.fee)}</td>
                                                                                            <td className={styles['daily-total-value']}>{fmt(totals.feeGst)}</td>
                                                                                        </tr>
                                                                                    </tfoot>
                                                                                );
                                                                            })()}
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                </Surface>
        </PageFrame>
    );
};

export default ManagementFeeDetail;
